"use client";

import { useMemo, useRef, useState } from "react";
import { cn } from "@/lib/cn";
import { ImagePlaceholder } from "@/components/ui/image-placeholder";
import { SpecimenLabel } from "@/components/ui/specimen-label";
import type { Product } from "@/content/types";

export function ProductGallery({
  gallery,
  originLabel,
  line,
}: Pick<Product, "gallery" | "originLabel" | "line">) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [current, setCurrent] = useState(0);
  const total = gallery.length;

  const labels = useMemo(
    () => gallery.map((item) => item.label.replace(/^素材位[：:]\s*/, "")),
    [gallery],
  );

  const updateCurrent = () => {
    const node = containerRef.current;
    if (!node) return;

    const nextIndex = Math.round(node.scrollLeft / Math.max(node.clientWidth, 1));
    setCurrent(Math.min(Math.max(nextIndex, 0), total - 1));
  };

  return (
    <section className="product-gallery relative">
      <div
        ref={containerRef}
        onScroll={updateCurrent}
        className="flex snap-x snap-mandatory overflow-x-auto scroll-smooth [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {labels.map((label, index) => (
          <div key={`${label}-${index}`} className="relative w-full shrink-0 snap-center">
            <ImagePlaceholder
              label={label}
              ratio={gallery[index]?.ratio ?? "1:1"}
              src={gallery[index]?.src}
              alt={gallery[index]?.alt}
              objectPosition={gallery[index]?.objectPosition}
              preload={index === 0}
              className="border-x-0 border-t-0 border-b-line"
            />
            {index === 0 && line !== "herbal" ? (
              <SpecimenLabel
                code={originLabel.code}
                name={originLabel.name}
                variant="origin"
                tone={line}
                className="absolute top-4 left-4"
              />
            ) : null}
          </div>
        ))}
      </div>
      <div className="pointer-events-none absolute right-4 bottom-4 rounded-md bg-paper/90 px-2.5 py-1 font-mono text-[11px] leading-4 tracking-[0.12em] text-ink">
        {current + 1} / {total}
      </div>
      <div className="mt-2 flex justify-center gap-1.5 px-5">
        {labels.map((label, index) => (
          <span
            key={`${label}-dot`}
            aria-hidden="true"
            className={cn(
              "block h-1.5 rounded-full bg-line-strong motion-safe:transition-[width,background-color] motion-safe:duration-200",
              index === current
                ? line === "hirudin"
                  ? "w-5 bg-hirudin"
                  : "w-5 bg-forest-700"
                : "w-1.5",
            )}
          />
        ))}
      </div>
    </section>
  );
}
